import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import { Box, Typography, Divider } from "@material-ui/core";
import AppInput from "components/Input/AppInput";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import { useDispatch } from "react-redux";
import { submitIntakeLinkContacts } from "store/slices/client";
import { showToast } from "store/slices/toast";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";

const IntakeLinkContacts = ({ clientData, token, onNextStep }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const initialValues = {
    emergencyContactFirstName: clientData?.emergencyContactResponse?.firstName || "",
    emergencyContactLastName: clientData?.emergencyContactResponse?.lastName || "",
    emergencyContactRelationship: clientData?.emergencyContactResponse?.relationship || "",
    emergencyContactEmail: clientData?.emergencyContactResponse?.email || "",
    emergencyContactPhone: clientData?.emergencyContactResponse?.phone || "",
    
    gpDoctorFirstName: clientData?.gpResponse?.firstName || "",
    gpDoctorLastName: clientData?.gpResponse?.lastName || "",
    gpPracticeName: clientData?.gpResponse?.practiceName || "",
    gpDoctorPhone: clientData?.gpResponse?.phone || "",
    gpAddress: clientData?.gpResponse?.address || "",
    
    supportCoordinatorFirstName: clientData?.supportCoordinatorResponse?.firstName || "",
    supportCoordinatorLastName: clientData?.supportCoordinatorResponse?.lastName || "",
    supportCoordinatorOrganizationName: clientData?.supportCoordinatorResponse?.organizationName || "",
    supportCoordinatorEmail: clientData?.supportCoordinatorResponse?.email || "",
    supportCoordinatorMobile: clientData?.supportCoordinatorResponse?.phone || "",
    
    planManagerContactName: clientData?.planManagerContactName || "",
    planManagerOrganizationName: clientData?.planManagerOrganizationName || "",
    planManagerEmail: clientData?.planManagerEmail || "",
    planManagerMobile: clientData?.planManagerPhone || "",
  };

  const validationSchema = Yup.object().shape({
    emergencyContactFirstName: Yup.string().required(t("validation.required")).max(64, "validation.max_length_64"),
    emergencyContactLastName: Yup.string().required(t("validation.required")).max(64, "validation.max_length_64"),
    emergencyContactRelationship: Yup.string().required(t("validation.required")).max(255, "validation.max_length_255"),
    emergencyContactEmail: Yup.string().required(t("validation.required")).max(255, "validation.max_length_255"),
    emergencyContactPhone: Yup.string().required(t("validation.required")).max(64, "validation.max_length_64"),

    gpDoctorFirstName: Yup.string().required(t("validation.required")).max(64, "validation.max_length_64"),
    gpDoctorLastName: Yup.string().required(t("validation.required")).max(64, "validation.max_length_64"),
    gpPracticeName: Yup.string().required(t("validation.required")).max(255, "validation.max_length_255"),
    gpDoctorPhone: Yup.string().required(t("validation.required")).max(64, "validation.max_length_64"),
    gpAddress: Yup.string().required(t("validation.required")).max(255, "validation.max_length_255"),

    supportCoordinatorFirstName: Yup.string().required(t("validation.required")).max(64, "validation.max_length_64"),
    supportCoordinatorLastName: Yup.string().required(t("validation.required")).max(64, "validation.max_length_64"),
    supportCoordinatorOrganizationName: Yup.string().required(t("validation.required")).max(255, "validation.max_length_255"),
    supportCoordinatorEmail: Yup.string().required(t("validation.required")).max(255, "validation.max_length_255"),
    supportCoordinatorMobile: Yup.string().required(t("validation.required")).max(64, "validation.max_length_64"),

    planManagerContactName: Yup.string().required(t("validation.required")).max(255, "validation.max_length_255"),
    planManagerOrganizationName: Yup.string().required(t("validation.required")).max(255, "validation.max_length_255"),
    planManagerEmail: Yup.string().required(t("validation.required")).max(255, "validation.max_length_255"),
    planManagerMobile: Yup.string().required(t("validation.required")).max(64, "validation.max_length_64"),
  });

  const handleSubmit = async (values) => {
    setLoading(true);
    const payload = {
      intakeLinkToken: token,
      emergencyContactJson: {
        firstName: values.emergencyContactFirstName,
        lastName: values.emergencyContactLastName,
        email: values.emergencyContactEmail,
        phone: values.emergencyContactPhone,
        relationship: values.emergencyContactRelationship
      },
      gp: {
        firstName: values.gpDoctorFirstName,
        lastName: values.gpDoctorLastName,
        practiceName: values.gpPracticeName,
        phone: values.gpDoctorPhone,
        address: values.gpAddress
      },
      supportCoordinator: {
        firstName: values.supportCoordinatorFirstName,
        lastName: values.supportCoordinatorLastName,
        email: values.supportCoordinatorEmail,
        phone: values.supportCoordinatorMobile,
        organizationName: values.supportCoordinatorOrganizationName
      },
      planManagerContactName: values.planManagerContactName,
      planManagerEmail: values.planManagerEmail,
      planManagerPhone: values.planManagerMobile,
      planManagerOrganizationName: values.planManagerOrganizationName,
    };

    try {
      const response = await dispatch(submitIntakeLinkContacts(payload));
      if (response && response.payload) {
        onNextStep(true);
      }
    } catch (e) {
      console.log(e);
      dispatch(showToast({ message: "Failed to submit Contacts", type: "error" }));
    }
    setLoading(false);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid }) => (
        <Form>
          <Typography variant="h6" style={{ color: AppColor.mainColor, marginBottom: 15 }}>
            {t("label.contacts")}
          </Typography>
          <Divider style={{ marginBottom: 20 }} />
          
          <Typography variant="subtitle1" style={{ fontWeight: "bold", marginTop: 20, marginBottom: 10 }}>{t("label.emergency_contact")}</Typography>
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="emergencyContactFirstName" label={t("label.firstName")} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="emergencyContactLastName" label={t("label.lastName")} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="emergencyContactRelationship" label={t("label.relationship")} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="emergencyContactEmail" label={t("label.email")} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="emergencyContactPhone" label={t("label.phone")} required />
            </div>
          </div>

          <Typography variant="subtitle1" style={{ fontWeight: "bold", marginTop: 20, marginBottom: 10 }}>{t("label.gp_details")}</Typography>
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="gpDoctorFirstName" label={t("label.firstName")} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="gpDoctorLastName" label={t("label.lastName")} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="gpPracticeName" label={t("label.practice_name")} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="gpDoctorPhone" label={t("label.phone")} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="gpAddress" label={t("label.address")} required />
            </div>
          </div>

          <Typography variant="subtitle1" style={{ fontWeight: "bold", marginTop: 20, marginBottom: 10 }}>{t("label.supportCoordinator")}</Typography>
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="supportCoordinatorFirstName" label={t("label.firstName")} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="supportCoordinatorLastName" label={t("label.lastName")} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="supportCoordinatorOrganizationName" label={t("label.organizationName")} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="supportCoordinatorEmail" label={t("label.email")} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="supportCoordinatorMobile" label={t("label.phone")} required />
            </div>
          </div>

          <Typography variant="subtitle1" style={{ fontWeight: "bold", marginTop: 20, marginBottom: 10 }}>{t("label.planManager")}</Typography>
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="planManagerContactName" label={t("label.contact_name")} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="planManagerOrganizationName" label={t("label.organizationName")} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="planManagerMobile" label={t("label.phone")} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="planManagerEmail" label={t("label.email")} required />
            </div>
          </div>

          <Box mt={4} display="flex" justifyContent="flex-end">
            <AppButton
              label={t("button.saveAndNext")}
              type="submit"
              buttonColor={AppColor.mainColor}
              isLoading={loading}
              disabled={!isValid || loading}
              style={{ width: "150px" }}
            />
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default IntakeLinkContacts;
